import { useState } from 'react'
import { Icon, TextField } from '@bloo-team/ui'
import { Play, Plus, Square } from '@bloo-team/ui/icons'
import { blockKind } from '@bloo-team/api-client'
import type { BlockKind, WorkbookBlock } from '@bloo-team/api-client'
import { chartSourceFor, useBlockResult } from '../../lib/workbook-run'
import type { BlockRun, UseWorkbookRun } from '../../lib/workbook-run'
import { BlockResultArea } from './BlockResults'
import type { EntityKind } from '@bloo-team/core-shell'
import { BLOCK_UI, INSERT_ORDER, UNKNOWN_ICON } from './blocks/registry'
import { VisualEditor, VisualView } from './blocks/VisualBlock'

/**
 * The Blocks tab of the workbook page (#205, design B · Tab): the view-mode
 * parameter bar, then the block cards, flush-left and full width. In Edit
 * every card carries move / delete, and a "+" between cards (and after the
 * last) inserts a block of any kind.
 */

export interface BlocksTabProps {
  clusterId: string
  scopeId: string
  blocks: WorkbookBlock[]
  editing: boolean
  parameters: Array<{ name: string; value: string }>
  /** Values typed in the view bar, by name — sent with the run. */
  paramValues: Record<string, string>
  onParamChange: (name: string, value: string) => void
  run: UseWorkbookRun
  onPatch: (i: number, next: WorkbookBlock) => void
  onInsert: (at: number, kind: BlockKind) => void
  onRemove: (i: number) => void
  onMove: (i: number, dir: -1 | 1) => void
  onPivot: (q: string) => void
  onEntity: (kind: EntityKind, value: string) => void
}

function labelOf(b: WorkbookBlock): string {
  const k = blockKind(b)
  return k ? BLOCK_UI[k].label : b.type
}

/* ------------------------------------------------------------ parameter bar */

function ParamBar({
  parameters, values, onChange,
}: {
  parameters: Array<{ name: string; value: string }>
  values: Record<string, string>
  onChange: (name: string, value: string) => void
}) {
  // Values filled here are for this viewing only (sent as `parameters` on run);
  // the parameters themselves are edited in Settings.
  return (
    <div className="wb-params" role="group" aria-label="Parameters">
      <span className="hud-eyebrow">parameters</span>
      {parameters.length === 0 ? (
        <p className="hud-unit wb-flush">This workbook has no parameters.</p>
      ) : (
        parameters.map((p) => (
          <TextField
            key={p.name}
            label={p.name}
            value={values[p.name] ?? p.value}
            onChange={(v) => onChange(p.name, v)}
          />
        ))
      )}
    </div>
  )
}

/* ------------------------------------------------------------ insert */

function InsertPicker({ onPick, label }: { onPick: (k: BlockKind) => void; label: string }) {
  return (
    <div className="wb-picker" role="menu" aria-label={label}>
      {INSERT_ORDER.map((k) => (
        <button key={k} type="button" role="menuitem" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => onPick(k)}>
          <Icon icon={BLOCK_UI[k].icon} size={13} />
          {BLOCK_UI[k].label}
        </button>
      ))}
    </div>
  )
}

function InsertSlot({ at, open, onToggle, onPick }: { at: number; open: boolean; onToggle: () => void; onPick: (k: BlockKind) => void }) {
  return (
    <div className="wb-insert" data-open={open || undefined}>
      <button
        type="button"
        className="wb-insert-btn"
        aria-label={`Insert a block before block ${at + 1}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Icon icon={Plus} size={12} />
      </button>
      {open ? <InsertPicker label={`Block type to insert at ${at + 1}`} onPick={onPick} /> : null}
    </div>
  )
}

/* ------------------------------------------------------------ card */

function BlockCard({
  clusterId, scopeId, block, index, count, editing, run, prevRun, running, onRun, onCancel,
  onPatch, onRemove, onMove, onPivot, onEntity, chartSource,
}: {
  clusterId: string
  scopeId: string
  block: WorkbookBlock
  index: number
  count: number
  editing: boolean
  run: BlockRun | undefined
  prevRun: BlockRun | undefined
  running: boolean
  onRun: () => void
  onCancel: () => void
  onPatch: (next: WorkbookBlock) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
  onPivot: (q: string) => void
  onEntity: (kind: EntityKind, value: string) => void
  /** Visualization only: the finished task of the block above, or null. */
  chartSource: string | null
}) {
  const kind = blockKind(block)
  const icon = kind ? BLOCK_UI[kind].icon : UNKNOWN_ICON
  // Fields of the previous block's data — Notification columns, Visualization keys.
  const prevResult = useBlockResult({
    clusterId, scopeId,
    taskId: editing && prevRun?.status === 'done' ? (prevRun.taskId ?? null) : null,
    pageNo: 1, pageSize: 1,
  })
  const prevFields = prevResult.data?.fields ?? []
  const editorProps = { block, clusterId, scopeId, index, onChange: onPatch, onRun, prevFields }

  let body: React.ReactNode
  if (kind === null) {
    body = (
      <div className="bui-alert bui-alert-info" role="note">
        <span className="bui-alert-mark">i</span>
        <span>{block.type} blocks are kept as they are; this page does not render them.</span>
      </div>
    )
  } else if (kind === 'visual') {
    body = editing
      ? <VisualEditor {...editorProps} />
      : <VisualView block={block} clusterId={clusterId} scopeId={scopeId} sourceTaskId={chartSource} />
  } else {
    const ui = BLOCK_UI[kind]
    const View = ui.View
    body = editing ? <ui.Editor {...editorProps} /> : View ? <View block={block} clusterId={clusterId} scopeId={scopeId} /> : null
  }

  const mark = run?.status === 'failed' ? 'failed' : run?.status === 'done' ? 'done' : null
  return (
    <section id={`wb-block-${index}`} className="hunt-block" data-active={running || undefined} aria-label={`Block ${index + 1}: ${labelOf(block)}`}>
      <div className="hunt-block-gutter">
        {kind !== null && kind !== 'text' && kind !== 'visual' ? (
          <button
            type="button"
            className="hunt-block-run"
            data-running={running || undefined}
            onClick={running ? onCancel : onRun}
            aria-label={running ? `Stop block ${index + 1}` : `Run block ${index + 1}`}
            title={running ? 'Stop' : 'Run'}
          >
            <Icon icon={running ? Square : Play} size={12} />
          </button>
        ) : null}
        <span className="hunt-block-idx">{index + 1}</span>
        {mark ? (
          <span className="hunt-block-mark" data-st={mark} title={mark === 'done' ? 'ran' : 'failed'}>
            {mark === 'done' ? '✓' : '✕'}
          </span>
        ) : null}
      </div>
      <div className="hunt-block-body">
        <div className="hunt-block-head">
          {/* Tier 1: what the block is — its type, in a tile tinted by its role family. */}
          <h3 className="wb-title">
            <span className="wb-type" data-family={kind ? BLOCK_UI[kind].family : undefined} aria-hidden="true">
              <Icon icon={icon} size={14} />
            </span>
            {labelOf(block)}
          </h3>
          {editing ? (
            <span className="hud-row wb-gap-1">
              <button type="button" className="row-action" aria-label={`Move block ${index + 1} up`} disabled={index === 0} onClick={() => onMove(-1)}>↑</button>
              <button type="button" className="row-action" aria-label={`Move block ${index + 1} down`} disabled={index === count - 1} onClick={() => onMove(1)}>↓</button>
              <button type="button" className="row-action" aria-label={`Delete block ${index + 1}`} onClick={onRemove}>✕</button>
            </span>
          ) : null}
        </div>
        {body}
        {kind !== null && kind !== 'visual' && kind !== 'text' ? (
          <BlockResultArea clusterId={clusterId} scopeId={scopeId} block={block} index={index} run={run} onPivot={onPivot} onEntity={onEntity} />
        ) : null}
      </div>
    </section>
  )
}

/* ------------------------------------------------------------ tab */

export function BlocksTab({
  clusterId, scopeId, blocks, editing, parameters, paramValues, onParamChange, run, onPatch, onInsert, onRemove, onMove, onPivot, onEntity,
}: BlocksTabProps) {
  const [picker, setPicker] = useState<number | null>(null)
  const insert = (at: number, k: BlockKind) => {
    onInsert(at, k)
    setPicker(null)
  }
  return (
    <div className="wb-col">
      {!editing ? <ParamBar parameters={parameters} values={paramValues} onChange={onParamChange} /> : null}
      {blocks.length === 0 && !editing ? (
        <p className="hud-unit" role="status">This workbook has no blocks.</p>
      ) : null}
      {blocks.map((b, i) => (
        <div key={i} className="wb-col">
          {editing ? (
            <InsertSlot at={i} open={picker === i} onToggle={() => setPicker(picker === i ? null : i)} onPick={(k) => insert(i, k)} />
          ) : null}
          <BlockCard
            clusterId={clusterId}
            scopeId={scopeId}
            block={b}
            index={i}
            count={blocks.length}
            editing={editing}
            run={run.runs[i]}
            prevRun={i > 0 ? run.runs[i - 1] : undefined}
            chartSource={chartSourceFor(blocks, run.runs, i)}
            running={run.activeIndex === i && run.runs[i]?.status === 'running'}
            onRun={() => run.run(i)}
            onCancel={run.cancel}
            onPatch={(next) => onPatch(i, next)}
            onRemove={() => onRemove(i)}
            onMove={(dir) => onMove(i, dir)}
            onPivot={onPivot}
            onEntity={onEntity}
          />
        </div>
      ))}
      {editing ? (
        <div className="wb-add-end">
          <button
            type="button"
            className="bui-btn bui-btn-outline bui-btn-sm"
            aria-expanded={picker === blocks.length}
            onClick={() => setPicker(picker === blocks.length ? null : blocks.length)}
          >
            <Icon icon={Plus} size={13} /> block
          </button>
          {picker === blocks.length ? <InsertPicker label="Block type to add" onPick={(k) => insert(blocks.length, k)} /> : null}
        </div>
      ) : null}
    </div>
  )
}
